import React, { useState, useEffect, FC } from "react";
import {
  Menu,
  MenuItem,
  Radio,
  ListItemText,
  Box,
  Typography,
  Divider,
  ListItemIcon,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useGetTenanatsHospitalsListQuery from "../../hooks/useGetTenanatsHospitalsListQuery";
import { setSelectedHospitalValue } from "../../hooks/useSelectedHospitalValue";
import { TENANT_HOSPITAL } from "../../types/CommonTypes";

interface SuperUserHospitalDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  tenantId?: string;
  hospitalId?: string;
}

const SuperUserHospitalDropdown: FC<SuperUserHospitalDropdownProps> = ({
  anchorEl,
  open,
  onClose,
  tenantId,
  hospitalId,
}) => {
  const [expandedTenant, setExpandedTenant] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [selectedHospital, setSelectedHospital] = useState<string | null>(null);

  const { data: tenantsHospitalsListData = [] } =
    useGetTenanatsHospitalsListQuery();

  useEffect(() => {
    if (tenantId && tenantsHospitalsListData?.length > 0) {
      setSelectedTenant(tenantId);
      setExpandedTenant(tenantId);
      const tenant = tenantsHospitalsListData?.find(
        (t: any) => t.tenantId === tenantId
      );
      if (tenant) {
        const hospital =
          tenant.hospitals.find((h: any) => h.uuid === hospitalId) ||
          tenant.hospitals[0];
        if (hospital) {
          setSelectedHospital(hospital.uuid);
          setSelectedHospitalValue(hospital);
        } else {
          setSelectedHospitalValue({
            uuid: hospitalId,
            tenantId: tenantId,
          });
        }
      }
    }
  }, [tenantId, hospitalId, tenantsHospitalsListData]);

  const handleSelection = (
    tenantId: string,
    hospitalId: string,
    hospitalName: string
  ) => {
    // Find the hospital data to get its region
    const hospitalData = tenantsHospitalsListData
      .flatMap((tenant) => tenant.hospitals)
      .find((h) => h.uuid === hospitalId);

    setSelectedTenant(tenantId);
    setSelectedHospital(hospitalId);
    setSelectedHospitalValue({
      ...hospitalData,
      uuid: hospitalId,
      name: hospitalName,
      tenantId,
    });
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: 320,
            mt: 1.5,
            p: 0.5,
            borderRadius: 2,
            boxShadow: 3,
          },
        },
        list: {
          disablePadding: true,
        },
      }}
    >
      {tenantsHospitalsListData.map((tenant: TENANT_HOSPITAL) => (
        <Box key={tenant.tenantId}>
          <MenuItem
            onClick={() => {
              setExpandedTenant((prev) =>
                prev === tenant.tenantId ? null : tenant.tenantId
              );
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Box display="flex" alignItems="center">
                <Radio
                  checked={selectedTenant === tenant.tenantId}
                  onClick={(e) => {
                    e.stopPropagation();
                    const first = tenant.hospitals[0];
                    if (first) {
                      handleSelection(tenant.tenantId, first.uuid, first.name);
                    } else {
                      setSelectedHospital(null);
                      setSelectedTenant(tenant.tenantId);
                      setSelectedHospitalValue({
                        uuid: hospitalId,
                        tenantId: tenantId,
                      });
                    }
                    setExpandedTenant(tenant.tenantId);
                  }}
                  size="small"
                  sx={{
                    mr: 1,
                    color: "#000",
                    "&.Mui-checked": {
                      color: "#65558F",
                    },
                  }}
                />
                <Typography>{tenant.tenantName}</Typography>
              </Box>
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{
                  transition: "transform 0.2s ease-in-out",
                  transform:
                    expandedTenant === tenant.tenantId
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                }}
              />
            </Box>
          </MenuItem>

          {expandedTenant === tenant.tenantId &&
            tenant.hospitals.map((hospital) => (
              <MenuItem
                key={hospital.uuid}
                onClick={() =>
                  handleSelection(tenant.tenantId, hospital.uuid, hospital.name)
                }
                selected={selectedHospital === hospital.uuid}
                sx={{ pl: 6 }}
              >
                <ListItemText primary={hospital.name} />
                {selectedHospital === hospital.uuid && (
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <CheckIcon fontSize="small" />
                  </ListItemIcon>
                )}
              </MenuItem>
            ))}
          <Divider />
        </Box>
      ))}
    </Menu>
  );
};

export default SuperUserHospitalDropdown;
